import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import type { ExpenseCategoryOption } from "../../api/types";

// 管理雜支分類(業主 2026-09-30:「分類的部分可以讓用戶自行增減」)。
//
// **所有人**都可以增減(業主選的)。有雜支在用的不准刪(業主選的,回收桶裡的也算),
// 「其他」固定保留 —— 刪除鈕照樣畫出來但按不下去,旁邊寫原因:
// 不畫的話他會一直找那顆鈕在哪。

export interface CategoryManagerProps {
  categories: ExpenseCategoryOption[];
  /** 增減之後要重讀一次清單;新增的那一個順便選起來。 */
  onChanged: (created?: ExpenseCategoryOption) => void;
}

function whyNot(one: ExpenseCategoryOption): string {
  if (one.value === "OTHER") return "固定保留";
  if (one.in_use) return `${one.in_use} 筆在用`;
  return "";
}

export function CategoryManager({ categories, onChanged }: CategoryManagerProps) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const made = await api.post<ExpenseCategoryOption>("/expense-categories", { name });
      setName("");
      onChanged(made);
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "新增不了", "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (one: ExpenseCategoryOption) => {
    setBusy(true);
    try {
      await api.del(`/expense-categories/${one.value}`);
      onChanged();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="管理分類"
      className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] p-2"
    >
      <ul className="m-0 flex max-h-48 list-none flex-col gap-1 overflow-y-auto p-0">
        {categories.map((one) => {
          const reason = whyNot(one);
          return (
            <li key={one.value} className="flex items-center justify-between gap-2 text-sm text-[var(--text)]">
              <span className="truncate">{one.label}</span>
              <span className="flex shrink-0 items-center gap-2">
                {reason ? <span className="text-xs text-[var(--text-3)]">{reason}</span> : null}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`刪除「${one.label}」`}
                  title={reason ? `刪不掉:${reason}` : undefined}
                  icon={<Trash2 aria-hidden="true" size={14} />}
                  disabled={busy || one.deletable === false}
                  onClick={() => void remove(one)}
                />
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Input
            label="新分類名稱"
            value={name}
            maxLength={20}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void add();
              }
            }}
          />
        </div>
        <Button
          size="sm"
          icon={<Plus aria-hidden="true" size={14} />}
          disabled={busy || !name.trim()}
          onClick={() => void add()}
        >
          新增分類
        </Button>
      </div>
    </section>
  );
}
